import React from 'react'
import { Alert, Button, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { DMSConfig } from '../../../../services/config.service'

export interface ESignatureFinishSetupBlockProps {
  configFormData: DMSConfig | Partial<DMSConfig>
  disabled: boolean
  loading: boolean
  message: string | null
  messageVariant: 'success' | 'danger' | 'warning'
  consentUrl?: string | null
  redirectUri?: string | null
  /** Show Connect URL (dev/demo override) input; only true when backend DEBUG is on */
  showConnectUrlOverride?: boolean
  onConnectUrlOverrideChange: (v: string | null) => void
  onRunFinishSetup: () => void
}

export const ESignatureFinishSetupBlock: React.FC<ESignatureFinishSetupBlockProps> = ({
  configFormData,
  disabled,
  loading,
  message,
  messageVariant,
  consentUrl,
  redirectUri,
  showConnectUrlOverride = false,
  onConnectUrlOverrideChange,
  onRunFinishSetup,
}) => {
  const { t } = useTranslation()
  const showRedirectUriHelp = redirectUri && message && messageVariant === 'danger'

  return (
    <Form.Group className="mb-2">
      {showConnectUrlOverride && (
        <>
          <Form.Label>{t('docusign.connect.urlOverride')}</Form.Label>
          <Form.Control
            type="url"
            placeholder="https://xxxx.ngrok.io or https://xxxx.trycloudflare.com"
            value={String(configFormData.docusign_connect_url_override ?? '')}
            onChange={(e) => onConnectUrlOverrideChange(e.target.value || null)}
            disabled={disabled}
            autoComplete="off"
          />
          <Form.Text className="text-muted w-100 d-block">{t('docusign.connect.urlOverrideHelp')}</Form.Text>
        </>
      )}
      <Button
        type="button"
        variant="outline-primary"
        size="sm"
        className="mt-2"
        disabled={disabled || loading || !configFormData.docusign_enabled}
        onClick={onRunFinishSetup}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
            {t('docusign.finishSetup.loading')}
          </>
        ) : (
          t('docusign.finishSetup.button')
        )}
      </Button>
      {message && (
        <Alert variant={messageVariant} className="mt-2 mb-0 small">
          {message}
          {consentUrl && (
            <div className="mt-2">
              <Button variant="outline-primary" size="sm" onClick={() => window.open(consentUrl, '_blank', 'noopener,noreferrer')}>
                {t('docusign.connect.consentOpenButton')}
              </Button>
              <span className="ms-2 small text-muted">{t('docusign.connect.consentThenRetry')}</span>
            </div>
          )}
          {showRedirectUriHelp && (
            <div className="mt-2 small">
              <div className="text-muted">{t('docusign.connect.redirectUriHelp')}</div>
              <code className="d-block mt-1 p-2 bg-dark text-light rounded text-break" style={{ fontSize: '0.85em' }}>
                {redirectUri}
              </code>
            </div>
          )}
        </Alert>
      )}
      <Form.Text className="text-muted d-block mt-1">{t('docusign.finishSetup.help')}</Form.Text>
    </Form.Group>
  )
}
